/**
 * Ted #10 (#913) + Sawyer C3 — the Bookings list's CHECK-OUT households.
 *
 * One owner's checked-in visits, grouped so the desk checks the family out
 * together and the customer pays once. Until Sawyer C3 a grooming visit was
 * kept OUT of the card ("the tier confirmation is per visit, so it can never
 * be batched") and only non-grooming rows counted toward the two a card
 * needs — so a daycare + groom family got no card at all. The card now holds
 * every checked-in visit, the groom included; "Check out all" collects each
 * groom's tier in a step before anything is posted, which is the condition
 * that comment named.
 *
 * Unchanged rules: a service leg never forms or joins a household (#984 — it
 * checks out WITH its visit), the Outstanding view never groups (#988), and
 * one visit alone has nothing to combine with. One dog with two visits (a
 * daycare and a separate groom, or a boarding stay and its #22 combo groom)
 * DOES form a card (refuter 4): paying once for both is Sawyer's point.
 */

/** The fields grouping reads off a Bookings row (AdminBooking satisfies it). */
export interface HouseholdCheckoutRow {
  readonly id: string;
  readonly customer_id: string;
  readonly customer_name?: string | null;
  readonly service_type_id: string;
  readonly status: string;
  readonly total_cents: number;
  readonly dog_names?: readonly string[] | null;
  readonly isLeg?: boolean;
}

export interface HouseholdGroup<R extends HouseholdCheckoutRow = HouseholdCheckoutRow> {
  readonly customerId: string;
  readonly customerName: string | null;
  /** The visits "Check out all" runs: every checked-in non-leg visit, list order. */
  readonly rows: readonly R[];
  readonly dogNames: readonly string[];
  readonly totalCents: number;
  /** The grooming visits among `rows`, list order — each gets a tier step. */
  readonly groomingRowIds: readonly string[];
}

export type HouseholdView = string;

function uniqueNames(rows: readonly HouseholdCheckoutRow[]): string[] {
  const seen = new Set<string>();
  const out: string[] = [];
  for (const r of rows) {
    for (const name of r.dog_names ?? []) {
      if (seen.has(name)) continue;
      seen.add(name);
      out.push(name);
    }
  }
  return out;
}

export function groupCheckoutHouseholds<R extends HouseholdCheckoutRow>(
  rows: readonly R[],
  isGrooming: (row: R) => boolean,
  view: HouseholdView,
): Map<string, HouseholdGroup<R>> {
  const out = new Map<string, HouseholdGroup<R>>();
  if (view === 'outstanding') return out;
  const byCustomer = new Map<string, R[]>();
  for (const r of rows) {
    if (r.status !== 'checked_in') continue;
    if (r.isLeg === true) continue;
    const list = byCustomer.get(r.customer_id) ?? [];
    list.push(r);
    byCustomer.set(r.customer_id, list);
  }
  for (const [customerId, list] of byCustomer) {
    if (list.length < 2) continue;
    out.set(customerId, {
      customerId,
      customerName: list[0]?.customer_name ?? null,
      rows: list,
      dogNames: uniqueNames(list),
      totalCents: list.reduce((sum, r) => sum + r.total_cents, 0),
      groomingRowIds: list.filter(isGrooming).map((r) => r.id),
    });
  }
  return out;
}

/**
 * The card's meta suffix for its grooms: "· Hank's groom: confirm the tier at
 * checkout", or "· 2 grooms: confirm each tier at checkout". null when none.
 */
export function householdGroomingNote<R extends HouseholdCheckoutRow>(
  group: HouseholdGroup<R>,
  dogNameOf: (row: R) => string,
): string | null {
  const grooms = group.rows.filter((r) => group.groomingRowIds.includes(r.id));
  if (grooms.length === 0) return null;
  if (grooms.length === 1) return `· ${dogNameOf(grooms[0]!)}'s groom: confirm the tier at checkout`;
  return `· ${grooms.length} grooms: confirm each tier at checkout`;
}
